import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { useApi } from '@/lib/auth'
import { usePreviewData } from '../preview-data'

const roleSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  userCount: z.number(),
})

// GET /v1/admin/roles -> { success, data: AdminRole[] }
const rolesEnvelopeSchema = z.object({ success: z.boolean(), data: z.array(roleSchema) })
const okSchema = z.object({ success: z.boolean() }).passthrough()

export type AdminRole = z.infer<typeof roleSchema>

export const adminRolesQueryKey = ['admin', 'roles'] as const

/** Roles with user counts. */
export function useAdminRoles() {
  const api = useApi()
  const preview = usePreviewData()
  return useQuery({
    queryKey: preview ? ['preview', 'admin-roles'] : adminRolesQueryKey,
    queryFn: async (): Promise<AdminRole[]> => {
      const res = await api.get('/v1/admin/roles', rolesEnvelopeSchema)
      if (!res.success) throw new Error('Failed to fetch roles')
      return res.data
    },
    enabled: !preview,
    ...(preview ? { initialData: preview.adminRoles, staleTime: Number.POSITIVE_INFINITY } : {}),
  })
}

/** Pure: Clerk custom role key from a slug (`'store-ops'` -> `'org:store_ops'`); an already-prefixed id is kept. */
export function roleIdFromSlug(slug: string): string {
  const s = slug
    .trim()
    .toLowerCase()
    .replace(/^org:/, '')
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/^_+|_+$/g, '')
  return `org:${s}`
}

/** Pure: true when the role has users and so cannot be deleted (API answers 409). */
export const roleHasUsers = (role: Pick<AdminRole, 'userCount'>): boolean => role.userCount > 0

const invalidateRoles = (qc: ReturnType<typeof useQueryClient>) =>
  Promise.all([
    qc.invalidateQueries({ queryKey: adminRolesQueryKey }),
    qc.invalidateQueries({ queryKey: ['admin', 'permissions'] }),
  ])

/** `mutate({ id, name, description? })` POST /v1/admin/roles. `id` must be `org:<slug>` (see `roleIdFromSlug`). */
export function useCreateRole() {
  const api = useApi()
  const qc = useQueryClient()
  const preview = usePreviewData()
  return useMutation({
    mutationFn: async (v: { id: string; name: string; description?: string }) => {
      if (preview) return { success: true }
      return api.request('/v1/admin/roles', { method: 'POST', body: v }, okSchema)
    },
    onSuccess: async () => {
      if (!preview) await invalidateRoles(qc)
    },
  })
}

/** `mutate({ roleId, name?, description? })` PATCH /v1/admin/roles/:roleId. */
export function useUpdateRole() {
  const api = useApi()
  const qc = useQueryClient()
  const preview = usePreviewData()
  return useMutation({
    mutationFn: async ({ roleId, ...body }: { roleId: string; name?: string; description?: string }) => {
      if (preview) return { success: true }
      return api.request(`/v1/admin/roles/${encodeURIComponent(roleId)}`, { method: 'PATCH', body }, okSchema)
    },
    onSuccess: async () => {
      if (!preview) await invalidateRoles(qc)
    },
  })
}

/** `mutate({ roleId })` DELETE /v1/admin/roles/:roleId. Rejects `ApiError` 409 when users are still assigned. */
export function useDeleteRole() {
  const api = useApi()
  const qc = useQueryClient()
  const preview = usePreviewData()
  return useMutation({
    mutationFn: async (v: { roleId: string }) => {
      if (preview) return { success: true }
      return api.request(`/v1/admin/roles/${encodeURIComponent(v.roleId)}`, { method: 'DELETE' }, okSchema)
    },
    onSuccess: async () => {
      if (!preview) await invalidateRoles(qc)
    },
  })
}
